/**
 * Window events for cross-screen navigation inside the dashboard (which uses section state, not routes).
 */

/** `new CustomEvent(OPEN_ADMIN_TAB_EVENT, { detail: 'agencySetup' })` opens Administration on that tab. */
export const OPEN_ADMIN_TAB_EVENT = 'open-admin-tab';

/**
 * Fired after a change that can move the agency setup checklist (agency profile save, EVV step,
 * user create, patient create). SetupChecklist listens and re-fetches its status.
 */
export const SETUP_STATUS_UPDATED_EVENT = 'setup-status-updated';

/** Legacy name fired by the EVV checklist; SetupChecklist listens for it too. */
export const EVV_SETUP_UPDATED_EVENT = 'evv-setup-updated';

/**
 * Fired after a change that can move the patient chart header (status, allergies, SOC checklist):
 * `new CustomEvent(PATIENT_CHART_UPDATED_EVENT, { detail: { patientId } })`. The patient header
 * summary listens and re-fetches. Detail carries the ID only, never PHI.
 */
export const PATIENT_CHART_UPDATED_EVENT = 'patient-chart-updated';

export const notifyPatientChartUpdated = (patientId: string): void => {
  window.dispatchEvent(new CustomEvent(PATIENT_CHART_UPDATED_EVENT, { detail: { patientId } }));
};

/**
 * Fired after a visit starts, ends or is closed (Phase 9B). The active-visit bar and My Day
 * listen and re-fetch. No detail (no PHI).
 */
export const VISIT_STATE_CHANGED_EVENT = 'visit-state-changed';

/** Admin tab id of Organization Account (plan, billing, exports). */
export const ORGANIZATION_ACCOUNT_TAB = 'organizationAccount';

/** Open Administration → Organization Account (subscription banners, cap banner). */
export const openOrganizationAccount = (): void => {
  window.dispatchEvent(new CustomEvent(OPEN_ADMIN_TAB_EVENT, { detail: ORGANIZATION_ACCOUNT_TAB }));
};

/**
 * Open a patient chart by ID from anywhere in the dashboard (Phase 13: after loading the sample
 * patient). `new CustomEvent(OPEN_PATIENT_CHART_EVENT, { detail: { patientId } })`; ID only, never PHI.
 */
export const OPEN_PATIENT_CHART_EVENT = 'open-patient-chart';

export const openPatientChartById = (patientId: string, tab?: string, visitId?: string): void => {
  // Phase 16F: an optional chart tab (subsection label, e.g. 'Prior Authorizations') for notification deep links.
  // Phase 18U U16: an optional visit id opens that visit's chart for review (Charting tab).
  window.dispatchEvent(new CustomEvent(OPEN_PATIENT_CHART_EVENT, { detail: { patientId, ...(tab ? { tab } : {}), ...(visitId ? { visitId } : {}) } }));
};

/**
 * Open a dashboard section (Phase 16F notification deep links):
 * `new CustomEvent(OPEN_SECTION_EVENT, { detail: { section: 'Schedule', subsection: 'Today' } })`.
 * Section and subsection are dashboard labels only, never PHI.
 */
export const OPEN_SECTION_EVENT = 'open-section';

export const openDashboardSection = (section: 'Schedule' | 'Billing', subsection?: string): void => {
  window.dispatchEvent(new CustomEvent(OPEN_SECTION_EVENT, { detail: { section, subsection } }));
};

/**
 * Fired by apiClient when the backend answers 402 ACCOUNT_READ_ONLY / ACCOUNT_SUSPENDED / ACCOUNT_CLOSED
 * (Phase 13): the account state changed during the session, so the signed-in app re-reads its
 * permissions. `detail: { code }` carries the domain code (no PHI).
 */
export const ACCOUNT_STATE_CHANGED_EVENT = 'account-state-changed';
